import { Command } from 'commander';
import chalk from 'chalk';
import { OutputFormatter, getJsonFlag } from '../lib/output.js';
import { MutagentError } from '../lib/errors.js';
import { performLoginAction, buildLoginJsonResponse } from '../lib/auth-flow.js';
import { runPostOnboarding } from './onboarding.js';

interface LoginOptions {
  endpoint: string;
  browser?: boolean;
  nonInteractive?: boolean;
}

/**
 * Top-level login command — THE CANONICAL user-facing entry point.
 *
 * Delegates to lib/auth-flow.ts performLoginAction(). `mutagent auth login`
 * is preserved as a back-compat alias and uses the same shared action.
 *
 * See: .memory/design-principles.md → "Login Unification"
 *      .memory/key-learnings/login-unification.md
 */
export function createLoginCommand(): Command {
  const login = new Command('login')
    .description('Login to MutagenT (browser OAuth — signup, onboard, authorize CLI in one flow)')
    .option('--browser', 'Force browser-based authentication')
    .option('--non-interactive', 'Disable interactive prompts (auto-selects browser auth)')
    .option('--endpoint <url>', 'API endpoint', 'https://api.mutagent.io')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent login                          ${chalk.dim('# Browser OAuth (recommended — handles signup + onboarding)')}
  ${chalk.dim('$')} mutagent login --browser                ${chalk.dim('# Force browser flow (skip method prompt)')}
  ${chalk.dim('$')} mutagent login --non-interactive        ${chalk.dim('# Auto-browser for AI agents and CI')}

${chalk.yellow('Browser OAuth flow (recommended for end users):')}
  Opens app.mutagent.io in your browser. You will:
    1. Sign in or sign up with your account
    2. Complete onboarding (workspace, provider config)
    3. Authorize this CLI session
  The CLI polls for completion and saves credentials when you authorize.

${chalk.yellow('Automation / CI / AI Agents:')}
  Set MUTAGENT_API_KEY environment variable before running:
    ${chalk.dim('$')} export MUTAGENT_API_KEY=mt_...
    ${chalk.dim('$')} mutagent login --json
  When MUTAGENT_API_KEY is set, login skips the browser flow entirely and
  validates the key directly. This is the canonical CI / agent path.

Environment Variables:
  MUTAGENT_API_KEY              Pre-existing API key (skips interactive flow)
  MUTAGENT_ENDPOINT             Custom API endpoint
  MUTAGENT_NON_INTERACTIVE=true Treat environment as non-interactive
  CI=true                       Automatically enables non-interactive mode

Verify after login:
  ${chalk.dim('$')} mutagent auth status                    ${chalk.dim('# Show session, workspace, org')}
  ${chalk.dim('$')} mutagent workspaces list --json         ${chalk.dim('# List available workspaces')}

${chalk.dim('Note: `mutagent auth login` is a back-compat alias and behaves identically.')}
    `)
    .action(async (options: LoginOptions) => {
      const isJson = getJsonFlag(login);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const result = await performLoginAction({
          endpoint: options.endpoint,
          browser: options.browser,
          nonInteractive: options.nonInteractive,
          isJson,
          output,
        });

        if (isJson) {
          output.output(buildLoginJsonResponse(result));
        } else {
          output.success('Logged in successfully');
          if (result.organization?.name) output.info(`Organization: ${result.organization.name}`);
          if (result.workspace?.name) output.info(`Workspace: ${result.workspace.name}`);
          output.info(`Endpoint: ${result.endpoint}`);
          output.info('Next: mutagent workspaces list --json');
        }

        // Post-onboarding wizard: only on first interactive login
        if (result.wasFirstLogin && process.stdin.isTTY && !isJson) {
          await runPostOnboarding();
        }
      } catch (error) {
        if (error instanceof MutagentError) {
          output.error(error.message);
          process.exit(error.exitCode);
        }
        throw error;
      }
    });

  return login;
}
